/**
 * The Board's search — Aaron, Oct 25, note 1.9 ("In board view, there needs
 * to be a search button, to the left of edit, that expands out into a search
 * bar when clicked") and answer 2.8 (task search must work); `docs/activity.md`
 * §8.5.
 *
 * A square button that opens into *Search tasks…* in the same place — left
 * of **Edit list** on a task list's board, left of **New task** on the
 * people's — and narrows the cards in every column as it is typed, by the
 * List's own rule (the server's `searchSql`): the task, its notes, its
 * checklist, what it is attached to, its categories, its people, its list
 * and its bucket. Each column keeps its *Completed*, holding the finished
 * ones that match. Escape or × closes it and clears it; leaving it empty
 * closes it too.
 */
import { useEffect, useRef, useState } from 'react'
import { cn } from '../../lib/cn.js'
import { Glyph } from '../glyphs.js'

export function BoardSearch({
  value, onChange, height,
}: { value: string; onChange: (q: string) => void; height: 34 | 36 | 40 }) {
  const [open, setOpen] = useState(value !== '')
  const input = useRef<HTMLInputElement>(null)
  const button = useRef<HTMLButtonElement>(null)
  const opened = useRef(false)
  useEffect(() => {
    if (open && opened.current) input.current?.focus()
    opened.current = false
  }, [open])

  const close = () => {
    onChange('')
    setOpen(false)
    window.setTimeout(() => button.current?.focus(), 0)
  }

  if (!open) {
    return (
      <button ref={button} type="button" aria-label="Search tasks" title="Search tasks"
        onClick={() => { opened.current = true; setOpen(true) }}
        className="inline-flex shrink-0 items-center justify-center rounded-lg border border-line bg-surface text-text-muted hover:bg-hover hover:text-text"
        style={{ width: height, height }}>
        <Glyph name="search" size={15} />
      </button>
    )
  }
  return (
    <label data-board-search
      className={cn('flex w-[250px] min-w-[150px] shrink items-center gap-2 rounded-[10px] border border-accent bg-surface pl-[11px] pr-1 text-[13px] text-text-faint',
        'shadow-[0_0_0_3px_rgb(var(--color-accent-ring))]')}
      style={{ height }}>
      <Glyph name="search" size={15} />
      <input ref={input} value={value} onChange={(event) => onChange(event.target.value)} placeholder="Search tasks…"
        aria-label="Search tasks"
        onKeyDown={(event) => { if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close() } }}
        onBlur={() => { if (!value.trim()) setOpen(false) }}
        className="min-w-0 flex-1 bg-transparent text-text outline-none placeholder:text-text-faint focus-visible:shadow-none" />
      <button type="button" aria-label="Close the search" onMouseDown={(event) => event.preventDefault()} onClick={close}
        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-text-muted hover:bg-hover hover:text-text">
        <Glyph name="close" size={13} />
      </button>
    </label>
  )
}
